import { AuthorityRequest, CompanyRequest, EditorCompany, EditorFile } from '@callcenter-app/service/order-editor.service';
import { authorityFileLabel } from '@callcenter-app/util/authority-file';
import { uinText } from '@callcenter-app/util/uin';

/**
 * The legal entity of step 2 (design §3.4, board «Заказ юрлица»): what the forms of the requisites (E22) and of the letter of authority (E23) hold while they are typed, which of the three states of
 * the board the block is in, what is not saved yet, and the two requests. Pure — the page keeps the draft, the server keeps the saved side (`model.company`).
 *
 * The three states: (1) the requisites form — the booking holds none, or the operator opened the saved ones with «Изменить»; (2) the requisites are saved, the letter of authority is not
 * (its form is open under the green card of the requisites); (3) both are saved — the card «Юрлицо · ЮЛ». The letter of authority can be left out: only the requisites are required to make the order.
 */

/** BIN / IIN: twelve digits; the control digit is the server's to judge (`uinInvalid`). */
export const UIN_LENGTH = 12;
/** The longest name, address and contract the server takes (design §2: fields of the requisites ≤ 300). */
export const COMPANY_TEXT_LIMIT = 300;
/** The representative of the letter: the server takes 150 characters (`field.wrongLength` beyond). */
export const REPRESENTATIVE_LIMIT = 150;
export const AUTHORITY_NUMBER_LIMIT = 100;
/** How many names of files of the letter the draft remembers. */
const FILE_NAMES_KEPT = 5;
/** `dd.MM.yyyy`. */
export const DATE_TEXT_LENGTH = 10;

export type CompanyEditing = 'company' | 'authority';

/** What the operator has typed into the forms of the legal entity and the server does not hold yet — the draft of step 2 keeps it. */
export interface CompanyDraft {
  title: string;
  /** Digits only. */
  uin: string;
  contract: string;
  address: string;
  representative: string;
  /** `dd.MM.yyyy` as it is typed. */
  date: string;
  number: string;
  /** The file of the saved letter was taken off with its cross: «Сохранить доверенность» takes it away (E23 `removeFile`). */
  fileRemoved: boolean;
  /** The form that is open over a saved card: the requisites («Изменить» of the green card) or the letter. Absent — the card as the server holds it. */
  editing?: CompanyEditing;
  /**
   * The names of the files the operator put for the letter, by the id of the file on the booking. The server names the file of the letter `letterOfAuthority.<расширение>` (the cash desk looks for that
   * name), so the name the operator knows is only here — while the page remembers it, the screen shows it; after that it says «Доверенность» with the extension.
   */
  fileNames?: Record<string, string>;
}

export const EMPTY_COMPANY_DRAFT: CompanyDraft = { title: '', uin: '', contract: '', address: '', representative: '', date: '', number: '', fileRemoved: false };

export type CompanyStage = 'company' | 'authority' | 'done';

/** The digits of what was typed or pasted («160 540 012 345» → «160540012345»), at most `max`. */
export function digitsOf(text: string, max: number): string {
  return text.replace(/\D/g, '').slice(0, max);
}

// the BIN as the board prints it («160 540 012 345») — one function for the editor and for the drawer of an order
export { uinText };

/** What is typed into the date of the letter becomes `dd.MM.yyyy`: the digits are grouped, the dots are put by themselves. */
export function maskDate(text: string): string {
  const d = digitsOf(text, 8);
  if (d.length <= 2) {
    return d;
  }
  return d.length <= 4 ? `${d.slice(0, 2)}.${d.slice(2)}` : `${d.slice(0, 2)}.${d.slice(2, 4)}.${d.slice(4)}`;
}

/** `dd.MM.yyyy` → `yyyy-MM-dd` when it is a real day of the calendar; none otherwise. */
export function isoOfDateText(text: string): string | undefined {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text.trim());
  if (!m) {
    return undefined;
  }
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const probe = new Date(year, month - 1, day, 12);
  const real = probe.getFullYear() === year && probe.getMonth() === month - 1 && probe.getDate() === day;
  return real && year >= 2000 ? `${m[3]}-${m[2]}-${m[1]}` : undefined;
}

/** `yyyy-MM-dd` → `dd.MM.yyyy`; what is not a date comes back as it is. */
export function dateTextOfIso(iso: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso ?? '';
}

/** The draft with nothing typed in it, and the names of the files the page remembers (they outlive the forms). */
export function emptyDraftKeeping(draft: Pick<CompanyDraft, 'fileNames'>): CompanyDraft {
  return draft.fileNames && Object.keys(draft.fileNames).length > 0 ? { ...EMPTY_COMPANY_DRAFT, fileNames: draft.fileNames } : EMPTY_COMPANY_DRAFT;
}

/** The draft with the name of the file the operator put remembered (the last few are kept). */
export function withFileName(draft: CompanyDraft, fileId: number, name: string): CompanyDraft {
  const kept = Object.entries(draft.fileNames ?? {}).filter(([id]) => id !== String(fileId)).slice(-(FILE_NAMES_KEPT - 1));
  return { ...draft, fileNames: Object.fromEntries([...kept, [String(fileId), name.slice(0, COMPANY_TEXT_LIMIT)]]) };
}

/**
 * The name of the file of the letter as the screen prints it: the one the page remembers from the operator's own file, else «Доверенность» with the extension of the file on the booking. Never the
 * server's own name (`letterOfAuthority.pdf`): it is the key the cash desk finds the file by, not a name for a person.
 */
export function authorityFileName(file: Pick<EditorFile, 'id' | 'name'>, draft: Pick<CompanyDraft, 'fileNames'> | undefined, word: string): string {
  const own = draft?.fileNames?.[String(file.id)]?.trim();
  return own || authorityFileLabel(file.name, word);
}

/**
 * The requisites are on the booking: the server says when they were saved (`savedAt` — the event is there), or the name and the number are there. NOT a full set of fields: the old editor does not
 * ask for the address, and the requisites it saved without one are the booking's all the same (the new server asks for the address only when the requisites are SAVED again:
 * `companyAddressMissing`). A booking with such requisites is not «with no requisites».
 */
export function isCompanySaved(company: EditorCompany | undefined): boolean {
  return !!company && (!!company.savedAt || (!!company.title?.trim() && !!company.uin));
}

/** Requisites that are saved with no address (an old editor wrote them): the card says so, and «Изменить» asks for the address. */
export function companyAddressMissing(company: EditorCompany | undefined): boolean {
  return isCompanySaved(company) && !company?.address?.trim();
}

export function companyStageOf(company: EditorCompany | undefined, draft: Pick<CompanyDraft, 'editing'>): CompanyStage {
  if (!isCompanySaved(company) || draft.editing === 'company') {
    return 'company';
  }
  return !company?.authority || draft.editing === 'authority' ? 'authority' : 'done';
}

/** The fields of the forms as the saved side has them — what «Изменить» opens. */
export function draftOfSaved(company: EditorCompany | undefined, editing: CompanyEditing): CompanyDraft {
  const authority = company?.authority;
  return {
    title: company?.title ?? '',
    uin: company?.uin ?? '',
    contract: company?.contract ?? '',
    address: company?.address ?? '',
    representative: authority?.representativeFullname ?? '',
    date: dateTextOfIso(authority?.letterOfAuthorityDate),
    number: authority?.letterOfAuthorityNumber ?? '',
    fileRemoved: false,
    editing,
  };
}

/** The files of the letter of authority the booking holds, newest last. */
export function authorityFilesOf(files: readonly EditorFile[]): EditorFile[] {
  return files.filter(f => f.kind === 'AUTHORITY');
}

/** The file of the letter the server keeps with it (saved), and the file that was put and is not attached yet (pending: the last one put). */
export function authorityFileState(company: EditorCompany | undefined, files: readonly EditorFile[]): { saved?: EditorFile; pending?: EditorFile } {
  const all = authorityFilesOf(files);
  const savedId = company?.authority?.fileId;
  const saved = savedId !== undefined ? all.find(f => f.id === savedId) : undefined;
  const pending = [...all].reverse().find(f => f.id !== savedId);
  return { ...(saved ? { saved } : {}), ...(pending ? { pending } : {}) };
}

/** The file the form shows: a new one over the saved one, nothing when the saved one was taken off. */
export function chosenAuthorityFile(company: EditorCompany | undefined, files: readonly EditorFile[], draft: Pick<CompanyDraft, 'fileRemoved'>): EditorFile | undefined {
  const { saved, pending } = authorityFileState(company, files);
  return pending ?? (draft.fileRemoved ? undefined : saved);
}

// ---------- the requests ----------

/** E22: complete requisites — a name, a BIN / IIN of twelve digits, an address. */
export function companyRequestOf(draft: CompanyDraft): CompanyRequest | undefined {
  const title = draft.title.trim();
  const address = draft.address.trim();
  const contract = draft.contract.trim();
  if (!title || draft.uin.length !== UIN_LENGTH || !address) {
    return undefined;
  }
  return { title, uin: draft.uin, address, ...(contract ? { contract } : {}) };
}

/** E23: the three fields of the letter; the file goes only when it is another than the saved one, and is taken away only with the explicit sign. */
export function authorityRequestOf(draft: CompanyDraft, company: EditorCompany | undefined, files: readonly EditorFile[]): AuthorityRequest | undefined {
  const representativeFullname = draft.representative.trim();
  const number = draft.number.trim();
  const date = isoOfDateText(draft.date);
  if (!representativeFullname || !number || !date) {
    return undefined;
  }
  const { saved, pending } = authorityFileState(company, files);
  return {
    representativeFullname,
    letterOfAuthorityDate: date,
    letterOfAuthorityNumber: number,
    ...(pending ? { fileId: pending.id } : {}),
    ...(!pending && saved && draft.fileRemoved ? { removeFile: true } : {}),
  };
}

// ---------- what is not saved ----------

function differs(typed: string, saved: string | undefined): boolean {
  return typed.trim() !== (saved ?? '').trim();
}

/** The requisites form holds something the server does not: the first form with anything typed, or the saved requisites opened and changed. */
export function companyFormDirty(company: EditorCompany | undefined, draft: CompanyDraft): boolean {
  if (!isCompanySaved(company)) {
    return draft.title.trim() !== '' || draft.uin !== '' || draft.contract.trim() !== '' || draft.address.trim() !== '';
  }
  return draft.editing === 'company'
    && (differs(draft.title, company?.title) || draft.uin !== (company?.uin ?? '') || differs(draft.contract, company?.contract) || differs(draft.address, company?.address));
}

/** The letter form holds something the server does not (only a form that is open can: an open one over nothing saved, or «Изменить» of the saved letter). */
export function authorityFormDirty(company: EditorCompany | undefined, files: readonly EditorFile[], draft: CompanyDraft): boolean {
  const { pending } = authorityFileState(company, files);
  const saved = company?.authority;
  if (!saved) {
    return draft.representative.trim() !== '' || draft.date !== '' || draft.number.trim() !== '' || !!pending;
  }
  return draft.editing === 'authority'
    && (differs(draft.representative, saved.representativeFullname) || draft.date !== dateTextOfIso(saved.letterOfAuthorityDate) || differs(draft.number, saved.letterOfAuthorityNumber) || !!pending || draft.fileRemoved);
}

/**
 * What the block of the legal entity says to «Создать заказ». The server needs no requisites to make the order (the domain does not, the old editor did not): it needs them to hand it over — so
 * `missing` (nothing saved, nothing typed) is a WARNING, the button stays on. `unsaved` — a form of the block holds what is not saved (the order would be made without it, and the operator would not
 * know) — blocks: it is not a rule of the server, it is the page not losing what was typed. The letter itself is optional — an empty form is not in the way.
 */
export type CompanyBlock = 'missing' | 'unsaved';

export function companyBlockOf(company: EditorCompany | undefined, files: readonly EditorFile[], draft: CompanyDraft): CompanyBlock | undefined {
  if (draft.editing === 'company' || companyFormDirty(company, draft) || authorityFormDirty(company, files, draft)) {
    return 'unsaved';
  }
  return isCompanySaved(company) ? undefined : 'missing';
}

/** The names of the files from the storage: ids that are numbers, names that are text, a few of them. */
function cleanFileNames(value: unknown): Pick<CompanyDraft, 'fileNames'> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const names = Object.entries(value as Record<string, unknown>)
    .filter((entry): entry is [string, string] => /^\d{1,12}$/.test(entry[0]) && typeof entry[1] === 'string' && entry[1].trim() !== '')
    .map(([id, name]) => [id, name.slice(0, COMPANY_TEXT_LIMIT)] as const)
    .slice(-FILE_NAMES_KEPT);
  return names.length > 0 ? { fileNames: Object.fromEntries(names) } : {};
}

/** The guard the page's draft needs: every field cut to its limit, nothing but strings. */
export function cleanCompanyDraft(value: Partial<Record<keyof CompanyDraft, unknown>>): CompanyDraft | undefined {
  const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.slice(0, max) : '');
  const draft: CompanyDraft = {
    title: text(value.title, COMPANY_TEXT_LIMIT),
    uin: digitsOf(text(value.uin, UIN_LENGTH * 2), UIN_LENGTH),
    contract: text(value.contract, COMPANY_TEXT_LIMIT),
    address: text(value.address, COMPANY_TEXT_LIMIT),
    representative: text(value.representative, REPRESENTATIVE_LIMIT),
    date: maskDate(text(value.date, DATE_TEXT_LENGTH * 2)),
    number: text(value.number, AUTHORITY_NUMBER_LIMIT),
    fileRemoved: value.fileRemoved === true,
    ...(value.editing === 'company' || value.editing === 'authority' ? { editing: value.editing } : {}),
    ...cleanFileNames(value.fileNames),
  };
  return JSON.stringify(draft) === JSON.stringify(EMPTY_COMPANY_DRAFT) ? undefined : draft;
}
